import * as React from 'react'
import dynamic from 'next/dynamic'
import styled from 'styled-components'

import { i18n } from '@thg-commerce/enterprise-core'
import { ProductVariantWithDescription } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import { FormFieldType } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { ChoiceSelectedVariantMap } from '@thg-commerce/enterprise-product-options/src/transformers/productOptionsMap'
import { VariantOptions } from '@thg-commerce/enterprise-product-options/src/types'
import {
  CustomDropdown,
  FormItem,
  ImageSwatch,
  Input,
} from '@thg-commerce/gravity-elements'
import {
  IconPosition,
  OptionsType,
} from '@thg-commerce/gravity-elements/CustomDropdown/CustomDropdown'
import { SelectedOptions, TextStyling } from '@thg-commerce/gravity-patterns'
import { spacing, Text } from '@thg-commerce/gravity-theme'

const ChevronUp = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronUp'),
  { loading: () => <div style={{ width: 24, height: 24 }} /> },
)

const ScrollToTopButton = styled.button<{
  buttonStyle?: {
    textStyle: TextStyling
  }
}>`
    display: flex;
    align-items: center;
    padding: ${spacing(1.5)} 0;
    color: ${(props) =>
      props.buttonStyle ? props.buttonStyle.textStyle.textColor : '#333333'};
    ${(props) =>
      Text(
        props.buttonStyle ? props.buttonStyle.textStyle.entry : 'bodyText',
        props.buttonStyle ? props.buttonStyle.textStyle.style : 'default',
      )}
}
`

export interface PersonalisationData {
  key?: string
  name?: string
  message?: string
  template?: string
  line1?: string
  line2?: string
  fontId?: string
}

export enum PersonalisationField {
  SingleSelect = 'SingleSelectionProductPersonalisationField',
  FreeText = 'FreeTextProductPersonalisationField',
  MultiSelect = 'MultiSelectionProductPersonalisationField',
}

interface PersonalisationFormProps {
  selectedVariant: ProductVariantWithDescription
  selectedImageSwatch?: string
  onImageChange?: (selectedOptions: SelectedOptions) => void
  personalisationData: PersonalisationData
  imageData?: ChoiceSelectedVariantMap | VariantOptions
  personalisationDataCallback: (data: PersonalisationData) => void
}

export const PersonalisationForm = (props: PersonalisationFormProps) => {
  const theme = useTheme()
  const personalisationFontsStyle =
    theme?.pageTheme?.productOptions?.personalisationFontsStyleOverride

  const personalisationFontOptionTypes: OptionsType[] =
    props?.selectedVariant?.personalisationData?.personalisationFonts &&
    props?.selectedVariant.personalisationData.personalisationFonts.length > 0
      ? props.selectedVariant.personalisationData.personalisationFonts.map(
          (personalisedFontOptionType) => ({
            key: personalisedFontOptionType?.name,
            displayText: personalisedFontOptionType?.name,
            value: personalisedFontOptionType?.name,
          }),
        )
      : []

  const scrollToTopButtonStyle =
    theme.pageTheme?.productOptions.scrollToTopButton

  const i18nText = {
    scrollToTopButtonText: i18n('product.scroltotopbutton.text'),
    selectDropdownLabelText: i18n('personalisation.dropdown.fonts.label'),
    imageSwatch: {
      label: i18n('personalisation.swatches.label'),
      showButtonText: i18n(
        'product.productoptions.imageswatch.button.show.text',
      ),
      showMoreButtonText: i18n(
        'product.productoptions.imageswatch.button.more.text',
      ),
      showLessButtonText: i18n(
        'product.productoptions.imageswatch.button.less.text',
      ),
    },
  }

  const {
    personalisationData,
    personalisationDataCallback,
    selectedVariant,
  } = props

  const callbackRef = React.useRef(personalisationDataCallback)
  const dataRef = React.useRef(personalisationData)

  React.useEffect(() => {
    callbackRef.current = personalisationDataCallback
  }, [personalisationDataCallback])

  React.useEffect(() => {
    dataRef.current = personalisationData
  }, [personalisationData])

  React.useEffect(() => {
    callbackRef.current({
      ...dataRef.current,
      template: selectedVariant.personalisationFields?.find(
        (field) => field.__typename === PersonalisationField.SingleSelect,
      )?.choices[0].key,
    })
  }, [selectedVariant])

  const personalisationFields = props.selectedVariant?.personalisationFields?.map(
    (field) => {
      if (field.__typename === PersonalisationField.FreeText) {
        return (
          <FormItem>
            <Input
              delete
              maxlength={field.maxLength}
              label={i18n(field.title)}
              required={field.required}
              type={FormFieldType.Text}
              value={props.personalisationData[field.name]}
              clearInputValueHandler={() => {
                props.personalisationDataCallback({
                  ...props.personalisationData,
                  [field.name]: '',
                })
              }}
              onChange={(event) => {
                props.personalisationDataCallback({
                  ...props.personalisationData,
                  [field.name]: event.target.value,
                })
              }}
            />
          </FormItem>
        )
      }

      if (field.__typename === PersonalisationField.SingleSelect) {
        const selectedImage =
          (field.choices &&
            field?.choices?.find(
              (choice) =>
                choice.title === (props.personalisationData?.template || ''),
            )?.key) ||
          field.choices[0].name

        return (
          <ImageSwatch
            label={i18nText.imageSwatch.label}
            labelHidden={false}
            required={field.required}
            ariaLabel={i18nText.imageSwatch.label}
            images={field.choices}
            i18nText={i18nText.imageSwatch}
            hideOptionalText={true}
            displayColoursAsTextOnly={true}
            selectedImage={selectedImage}
            onImageChange={(value: string) => {
              props.onImageChange?.({ selectedImageSwatch: value })
              field.choices &&
                props.personalisationDataCallback({
                  ...props.personalisationData,
                  [field.name]: field.choices?.find(
                    (choice) => choice.key === value,
                  )?.title,
                })
            }}
          />
        )
      }
      return
    },
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: `${spacing(3)}`,
      }}
    >
      {personalisationFields}
      {scrollToTopButtonStyle?.show && (
        <ScrollToTopButton
          onClick={() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            })
          }}
          buttonStyle={scrollToTopButtonStyle}
        >
          {i18nText.scrollToTopButtonText}
          <ChevronUp />
        </ScrollToTopButton>
      )}
      {personalisationFontsStyle?.show && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${spacing(3)}`,
          }}
        >
          <CustomDropdown
            label={i18nText.selectDropdownLabelText}
            removeLabelMargin={true}
            labelHidden={false}
            useCustomFontStyling={true}
            disabled={!props?.selectedVariant.inStock}
            aria-label={i18nText.selectDropdownLabelText}
            options={personalisationFontOptionTypes}
            onChange={(event) => {
              props.personalisationDataCallback({
                ...props.personalisationData,
                ['fontId']: event.value,
              })
            }}
            selectedIconPosition={IconPosition.RIGHT}
          />
        </div>
      )}
    </div>
  )
}
